import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';
import { useTranslation } from 'react-i18next';

import { useEditorStyles } from './styles';
import type { SortDirection } from './sortUtils';

interface ISortToggleButtonProps {
  direction: SortDirection;
  onToggle: () => void;
  testId: string;
}

/**
 * Ascending/descending sort toggle shared by every alphabetically-sorted list in the Editor
 * Mode tree (spots, hotspots, objects, AI scripts).
 *
 * Callers render this only when their own list has at least one row — an empty list has
 * nothing to sort, so the control disappears rather than sitting there disabled.
 *
 * @param props.direction - Current sort direction for the list this button controls.
 * @param props.onToggle - Flips the direction.
 * @param props.testId - `data-testid` for the underlying button.
 */
const SortToggleButton = ({
  direction,
  onToggle,
  testId,
}: ISortToggleButtonProps) => {
  const { t: translate } = useTranslation();
  const styles = useEditorStyles();
  const label = translate(
    direction === 'asc'
      ? 'editorMode.sortAscending'
      : 'editorMode.sortDescending',
    direction === 'asc'
      ? 'Sort ascending (A–Z, 0–9)'
      : 'Sort descending (Z–A, 9–0)',
  );

  return (
    <Tooltip title={label} placement="bottom" arrow>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        sx={styles.panelIconButton}
        aria-label={label}
        data-testid={testId}
      >
        <SortIcon
          fontSize="small"
          sx={{ transform: direction === 'desc' ? 'scaleY(-1)' : 'none' }}
        />
      </IconButton>
    </Tooltip>
  );
};

export default SortToggleButton;
